import { DateTime } from 'luxon';
import { useState } from 'react';
import { useSession } from '@/auth/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { supportingTypes } from '@/reports/index';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Icons,
  InputAdornment,
  Select,
  SelectOption,
  Text,
  Tooltip,
} from '@/theme/index';
import { useStatesOptions } from '@/utils/states';
import {
  AnswerContainer,
  CommentAgreement,
  ErrorContainer,
  OptionButton,
  Question,
  QuestionContainer,
  TextAreaAnswer,
} from './components';
import { ContactTypes, ScamTypes, SpamCategories } from './constants';
import {
  useCreateComment,
  CommentData,
} from '../CommentsSection/useCreateComment';

const SOURCE_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss ZZZ';
export interface CommentFormV2Props {
  /** The permalink of the report */
  permalink: string;
  /** Function to be called when the user adds a comment */
  onCommentAdded: (comment: supportingTypes.Comment) => void;
  /**
   * Function to be called when the user closes the comment form
   * mainly used to close when he form is used in a modal
   */
  onClose?: () => void;
}

/**
 * Form to add a comment to a report
 * This form is divided into 3 main steps
 * 1. Ask if the number is spam
 * 2. Ask for more details about the spam or scam call or message if the user answered yes in step 1
 * 3. Ask for the name of the commenter if the user answered no in step 1
 */
export function CommentFormV2({
  permalink,
  onCommentAdded,
  onClose,
}: CommentFormV2Props) {
  const {
    session: { account },
  } = useSession();
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const { logError } = useAppConfigContext();
  const { mutate: createComment } = useCreateComment(logError);

  const userCode = account?.account.user_info?.user_code || '';

  const [currentStep, setCurrentStep] = useState(1);
  const states = useStatesOptions();
  states.shift();

  // Form state

  const [spamCall, setSpamCall] = useState<CommentData['spammer'] | ''>('');
  const [spamCallError, setSpamCallError] = useState(false);
  const [contactType, setContanctType] = useState<
    CommentData['contact_type'] | ''
  >('');
  const [contactTypeError, setContactTypeError] = useState(false);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState(false);
  const [commenterName, setCommenterName] = useState('');
  const [spamCategory, setSpamCategory] = useState('');
  const [isScam, setIsScam] = useState(false);
  const [isOtherSpamCategory, setIsOtherSpamCategory] = useState(false);
  const [otherSpamCategory, setOtherSpamCategory] = useState('');
  const [scamType, setScamType] = useState('');
  const [isOtherScamType, setIsOtherScamType] = useState(false);
  const [otherScamType, setOtherScamType] = useState('');
  const [scamAmount, setScamAmount] = useState('');
  const [notApplicable, setNotApplicable] = useState(false);
  const [homeState, setHomeState] = useState('');
  const [homeCity, setHomeCity] = useState('');

  const resetForm = () => {
    setCurrentStep(1);
    setSpamCall('');
    setContanctType('');
    setComment('');
    setCommenterName('');
    setSpamCategory('');
    setIsScam(false);
    setIsOtherSpamCategory(false);
    setOtherSpamCategory('');
    setScamType('');
    setIsOtherScamType(false);
    setOtherScamType('');
    setScamAmount('');
    setNotApplicable(false);
    setHomeState('');
    setHomeCity('');
  };

  const getButtonVariant = (value: string, selectedValue: string) => {
    return value === selectedValue ? 'contained' : 'outlined';
  };

  // Event handlers

  const handleSpamCallChange = (value: CommentData['spammer']) => {
    if (!!value && value === spamCall) {
      setSpamCall('');

      return;
    }

    setSpamCall(value);
    setSpamCallError(false);
  };

  const handleContactTypeChange = (value: CommentData['contact_type']) => {
    if (!!value && value === contactType) {
      setContanctType('');

      return;
    }

    setContanctType(value);
    setContactTypeError(false);
  };

  const handleSpamCategorySelect = (value: string) => {
    setSpamCategory(value);
    setIsScam(false);
    setIsOtherSpamCategory(false);

    if (value === 'scam') {
      setIsScam(true);
    }

    if (value === 'other') {
      setIsOtherSpamCategory(true);
    }
  };

  const handleScamTypeSelect = (value: string) => {
    setScamType(value);
    setIsOtherScamType(false);

    if (value === 'other') {
      setIsOtherScamType(true);
    }
  };

  const handleScamAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    const isNumeric = /^[0-9]*\.?[0-9]*$/;

    if (isNumeric.test(value)) {
      setScamAmount(event.target.value);
    }
  };

  const handleFirstStepSubmit = (nextStep: number) => {
    if (!spamCall || !contactType || !comment) {
      if (!spamCall) {
        setSpamCallError(true);
      }

      if (!contactType) {
        setContactTypeError(true);
      }

      if (!comment) {
        setCommentError(true);
      }

      return;
    }

    setCurrentStep(nextStep);
  };

  const handleSubmitComment = () => {
    if (!spamCall || !contactType || !comment) {
      return;
    }

    let commentData: CommentData = {
      spammer: spamCall,
      contact_type: contactType,
      comment,
      commenter_name: commenterName || 'Anonymous',
      brand: brandName.toLowerCase(),
      user_id: userCode,
    };

    const addIfProvided = (key: string, value: string | undefined) => {
      if (value) {
        commentData = {
          ...commentData,
          [key]: value,
        };
      }
    };

    const findDisplayValue = (value: string, options: SelectOption[]) => {
      return options.find((option) => option.value === value)?.display;
    };

    function handleOtherOptionSelection(
      value: string,
      otherType: string,
      options: SelectOption[]
    ) {
      if (value) {
        const label = findDisplayValue(value, options);

        if (value === 'other' && otherType) {
          return `${label} - ${otherType}`;
        }

        return label;
      }

      return undefined;
    }

    addIfProvided(
      'opt_comment_category',
      handleOtherOptionSelection(
        spamCategory,
        otherSpamCategory,
        SpamCategories
      )
    );
    addIfProvided(
      'opt_scam_type',
      handleOtherOptionSelection(scamType, otherScamType, ScamTypes)
    );
    addIfProvided(
      'opt_scam_amount',
      notApplicable ? 'Not Applicable' : scamAmount
    );
    addIfProvided('home_state', homeState);
    addIfProvided('home_city', homeCity);

    createComment({ permalink, commentData });

    const localComment: supportingTypes.Comment = {
      name: commentData.commenter_name,
      source: {
        type: 'Web',
        name: brandName,
      },
      body: commentData.comment,
      datetime: {
        unix_timestamp: null,
        full: DateTime.local().toFormat(SOURCE_DATE_FORMAT),
      },
    };

    onCommentAdded(localComment);
    setCurrentStep(4);
  };

  const handleGoBack = () => {
    if (currentStep === 2 || currentStep === 3) {
      setCurrentStep(1);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    resetForm();
  };

  return (
    <Box data-testid="comment-form-v2">
      {[1, 2, 3].includes(currentStep) && (
        <Grid container justifyContent="space-between" marginBottom={2}>
          <Grid item>
            <Text variant="h4" display="flex" alignItems="center">
              <Icons.ModeCommentRounded color="primary" sx={{ mr: 1 }} />
              Add a comment
            </Text>
          </Grid>

          <Grid item>
            {onClose && (currentStep === 1 || currentStep === 4) && (
              <Button onClick={handleClose} data-testid="close-button">
                <Icons.Close sx={{ mr: 1 }} />
                Close
              </Button>
            )}
            {(currentStep === 2 || currentStep === 3) && (
              <Button onClick={handleGoBack} data-testid="back-button">
                <Icons.ArrowBack sx={{ mr: 1 }} /> Back
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      {currentStep === 1 && (
        <Box data-testid="comment-form-v2-step-1">
          <QuestionContainer>
            <Question sx={{ marginBottom: 0 }}>
              Was this number a spam or scam caller?
            </Question>
            <Tooltip
              title={
                <>
                  <Text variant="body2" marginBottom={1}>
                    <b>Spam Calls:</b> Spam refers to unsolicited phone calls
                    typically made for marketing purposes, often from automated
                    systems or robocallers. While annoying, spam calls usually
                    do not pose significant risk to the individual receiving the
                    call.
                  </Text>
                  <Text variant="body2">
                    <b>Scam Calls:</b> Scams are fraudulent attempts to deceive
                    individuals into providing personal information, money, or
                    access to sensitive accounts. They can pose significant risk
                    of financial loss or identity theft.
                  </Text>
                </>
              }
            >
              <Icons.Info fontSize="inherit" color="disabled" />
            </Tooltip>
          </QuestionContainer>
          <AnswerContainer>
            <Grid container spacing={2}>
              <Grid item xs={6} sm="auto">
                <OptionButton
                  variant={getButtonVariant('no', spamCall)}
                  color="inherit"
                  sx={(theme) => ({
                    width: { xs: '100%', sm: 'auto' },
                    '&.MuiButton-outlined': {
                      color: '#61BB6D',
                    },
                    '&.MuiButton-contained': {
                      color: theme.palette.common.white,
                      backgroundColor: '#61BB6D',
                    },
                  })}
                  onClick={() => handleSpamCallChange('no')}
                  data-testid="spam-call-no"
                >
                  Not Spam
                </OptionButton>
              </Grid>
              <Grid item xs={6} sm="auto">
                <OptionButton
                  variant={getButtonVariant('unsure', spamCall)}
                  color="success"
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                  onClick={() => handleSpamCallChange('unsure')}
                  data-testid="spam-call-unsure"
                >
                  Unsure
                </OptionButton>
              </Grid>
              <Grid item xs={12} sm="auto">
                <OptionButton
                  variant={getButtonVariant('yes', spamCall)}
                  color="error"
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                  onClick={() => handleSpamCallChange('yes')}
                  data-testid="spam-call-yes"
                >
                  Yes, Report Spam/Scam
                </OptionButton>
              </Grid>
            </Grid>
          </AnswerContainer>
          {spamCallError && (
            <ErrorContainer
              message="Please select an option"
              data-testid="spam-call-error"
            />
          )}

          <Question>What type of message did you receive?</Question>
          <AnswerContainer>
            {ContactTypes.map((option) => (
              <OptionButton
                color="success"
                variant={getButtonVariant(option.value, contactType)}
                onClick={() => handleContactTypeChange(option.value)}
                data-testid={`contact-type-${option.value}`}
                key={option.value}
              >
                {option.display}
              </OptionButton>
            ))}
          </AnswerContainer>
          {contactTypeError && (
            <ErrorContainer
              message="Please select an option"
              data-testid="contact-type-error"
            />
          )}

          <Question>
            Please describe what was said on the call or message you received
            and/or who the caller said they were
          </Question>
          <AnswerContainer>
            <TextAreaAnswer
              value={comment}
              rows={4}
              placeholder="Enter your comment here..."
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setComment(event.target.value);
                setCommentError(false);
              }}
              data-testid="contact-comment"
              error={commentError}
            />
          </AnswerContainer>
          {commentError && (
            <ErrorContainer
              message="Please provide a description of the call or message you received."
              data-testid="contact-comment-error"
            />
          )}

          {spamCall === 'unsure' || spamCall === 'yes' ? (
            <>
              <Question>
                Will you help the community by answering up to 3 more quick
                questions about this number?
              </Question>
              <AnswerContainer sx={{ gap: 4 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => handleFirstStepSubmit(3)}
                  data-testid="no-next"
                >
                  No, Submit Comment
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleFirstStepSubmit(2)}
                  data-testid="yes-next"
                >
                  Yes, Next
                </Button>
              </AnswerContainer>
            </>
          ) : (
            <AnswerContainer>
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleFirstStepSubmit(3)}
                data-testid="submit-comment"
              >
                Submit Comment
              </Button>
            </AnswerContainer>
          )}
        </Box>
      )}

      {currentStep === 2 && (
        <Box data-testid="comment-form-v2-step-2">
          <Question>
            Which option best categorizes the call or message you received?
          </Question>
          <AnswerContainer sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
            <Select
              id="spam-category"
              value={spamCategory}
              options={SpamCategories}
              placeholder="Select a category"
              size="small"
              aria-label="Select a category for the call or message you received."
              onOptionSelect={handleSpamCategorySelect}
              data-testid="spam-category"
            />
            {isOtherSpamCategory && (
              <TextAreaAnswer
                id="other-span-category"
                value={otherSpamCategory}
                placeholder="Enter category"
                rows={1}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setOtherSpamCategory(event.target.value)
                }
                data-testid="other-spam-category"
              />
            )}
          </AnswerContainer>

          {isScam && (
            <>
              <Question>
                What was the suspected scam call or message related to?
              </Question>
              <AnswerContainer
                sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
              >
                <Select
                  id="scam-type"
                  value={scamType}
                  options={ScamTypes}
                  placeholder="Select one"
                  size="small"
                  aria-label="Select the type of scam related to the call or message you received."
                  onOptionSelect={handleScamTypeSelect}
                  data-testid="scam-type"
                />
                {isOtherScamType && (
                  <TextAreaAnswer
                    id="other-scam-type"
                    value={otherScamType}
                    placeholder="Enter type"
                    rows={1}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setOtherScamType(event.target.value)
                    }
                    data-testid="other-scam-type"
                  />
                )}
              </AnswerContainer>

              <Question sx={{ mb: 0.5 }}>
                Approximately how much money did they attempt to scam?
              </Question>
              <AnswerContainer>
                <TextAreaAnswer
                  id="scam-amount"
                  value={scamAmount}
                  placeholder="Enter amount"
                  rows={1}
                  type="number"
                  fullWidth={false}
                  onChange={handleScamAmountChange}
                  data-testid="scam-amount"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => setNotApplicable(event.target.checked)}
                        data-testid="not-applicable"
                      />
                    }
                    label="Not Applicable"
                  />
                </Box>
              </AnswerContainer>
            </>
          )}

          <Question sx={{ mb: 0.5 }}>
            What city and state is your area code associated with?
          </Question>
          <Text variant="body2">
            Your feedback will help us pinpoint what areas scammers are
            targeting
          </Text>
          <AnswerContainer>
            <Select
              id="scam-state"
              value={homeState}
              options={states}
              placeholder="Select a state"
              size="small"
              aria-label="Select the state where you received the call or message."
              onOptionSelect={(value: string) => setHomeState(value)}
              data-testid="scam-state"
            />
            <TextAreaAnswer
              id="scam-city"
              value={homeCity}
              placeholder="Enter City"
              rows={1}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setHomeCity(event.target.value)
              }
              data-testid="scam-city"
            />
          </AnswerContainer>

          <Question>Name to associate with this comment</Question>
          <AnswerContainer>
            <TextAreaAnswer
              placeholder="Anonymous"
              value={commenterName}
              rows={1}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCommenterName(event.target.value)
              }
              data-testid="commenter-name"
            />
          </AnswerContainer>

          <CommentAgreement />

          <AnswerContainer>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmitComment}
              data-testid="submit-comment"
            >
              Submit Comment
            </Button>
          </AnswerContainer>
        </Box>
      )}
      {currentStep === 3 && (
        <Box data-testid="comment-form-v2-step-3">
          <Question>Name to associate with this comment</Question>
          <AnswerContainer>
            <TextAreaAnswer
              placeholder="Anonymous"
              value={commenterName}
              rows={1}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setCommenterName(event.target.value)
              }
              data-testid="commenter-name"
            />
          </AnswerContainer>

          <CommentAgreement />

          <AnswerContainer>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmitComment}
              data-testid="submit-comment"
            >
              Submit Comment
            </Button>
          </AnswerContainer>
        </Box>
      )}

      {currentStep === 4 && (
        <Box data-testid="comment-form-v2-step-4" my={2}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box flexGrow={1}>
              <Text variant="h4">Thank you</Text>
              <Text variant="body1">
                Your feedback will help others stay safe, and grow our community
              </Text>
            </Box>
            <Box>
              <Icons.EmojiEmotions
                fontSize="large"
                color="secondary"
                sx={{ width: '100%', height: '100%', maxHeight: 96 }}
              />
            </Box>
          </Box>

          <Box mt={2} textAlign="center">
            <Button
              variant="contained"
              onClick={handleClose}
              data-testid="close-comment-form"
            >
              Close
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
